import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/utils/format';
import Modal from './Modal';
import type { BlockedIp } from '@/hooks/useAdminData';

type IpsTabProps = {
  blockedIps: BlockedIp[];
  managerEmail: string;
  onBlock: (ip: string, reason: string, manager: string) => void;
  onUnblock: (ipId: string, manager: string) => void;
};

export default function IpsTab({ blockedIps, managerEmail, onBlock, onUnblock }: IpsTabProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [ip, setIp] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openForm = () => {
    setIp('');
    setReason('');
    setError(null);
    setShowForm(true);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ip.trim() || !reason.trim()) {
      setError(t('admin.ips.form.errorRequired'));
      return;
    }
    onBlock(ip, reason, managerEmail);
    setShowForm(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-semibold text-foreground-950">
            {t('admin.ips.title')}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">{t('admin.ips.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={openForm}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
        >
          <i className="ri-add-line"></i>
          {t('admin.ips.add')}
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.ips.ip')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.ips.reason')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.ips.blockedAt')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.ips.blockedBy')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.ips.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {blockedIps.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.ips.empty')}
                  </td>
                </tr>
              ) : (
                blockedIps.map((entry) => (
                  <tr key={entry.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-5 py-3">
                      <span className="rounded-md bg-foreground-100 px-2 py-1 font-mono text-xs font-semibold text-foreground-800">
                        {entry.ip}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{entry.reason}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-foreground-600">
                      {formatDateTime(entry.blockedAt)}
                    </td>
                    <td className="px-5 py-3 text-foreground-600">{entry.blockedBy}</td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => onUnblock(entry.id, managerEmail)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        <i className="ri-global-line"></i>
                        {t('admin.ips.unblock')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <Modal title={t('admin.ips.form.title')} subtitle={t('admin.ips.form.subtitle')} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.ips.form.ipLabel')}
              </label>
              <input
                type="text"
                value={ip}
                onChange={(event) => setIp(event.target.value)}
                placeholder={t('admin.ips.form.ipPh')}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-mono text-sm text-foreground-950 outline-none transition-colors placeholder:font-sans placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.ips.form.reasonLabel')}
              </label>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('admin.ips.form.reasonPh')}
                maxLength={200}
                rows={3}
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                {t('admin.ips.form.cancel')}
              </button>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
              >
                {t('admin.ips.form.submit')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}